import { useEffect } from 'react'
import { useQuery, useInfiniteQuery } from '@tanstack/react-query'
import { fetchAlerts, fetchAlert, fetchAlertGeoJSON } from '../api/alerts'
import { useAlertStore } from '../store/alertStore'
import type { AlertFilters } from '../types/cap'

const MAP_SEED_FILTERS: AlertFilters = {
  hazard_types: [],
  severity: [],
  active_only: true,
  min_priority: 1,
  search: '',
  region: null,
}

/** Fetches all active alerts (up to 1500) and seeds the store — used by the map layer.
 *
 * The old 500 cap silently excluded entire priority tiers once national active-alert volume grew
 * past it — active alerts are fetched priority DESC, so once >=500 alerts have priority >=2, every
 * single priority-1 alert (e.g. all of ACT ESA's ambulance/routine-fire dispatches, which sit at the
 * bottom of the severity matrix by design) drops off the map entirely, not just visually — they
 * never even reach the client (caught in production 2026-08-14, volume had reached 833 active with
 * 636 at priority >=2). 1500 gives real headroom above that; if volume keeps growing, a viewport/bbox
 * -scoped query would be a better fix than raising this further. */
export function useMapAlerts() {
  const upsertAlert = useAlertStore((s) => s.upsertAlert)
  const result = useQuery({
    queryKey: ['map-alerts'],
    queryFn: () => fetchAlerts({ filters: MAP_SEED_FILTERS, limit: 1500, offset: 0 }),
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
  useEffect(() => {
    if (!result.data) return
    result.data.items.forEach((a) => upsertAlert(a))
  }, [result.data, upsertAlert])
  return result
}

export function useAlertList(filters: AlertFilters, limit = 50) {
  return useInfiniteQuery({
    queryKey: ['alerts', filters],
    queryFn: ({ pageParam = 0 }) =>
      fetchAlerts({ filters, limit, offset: pageParam as number }),
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((acc, p) => acc + p.items.length, 0)
      return loaded < last.total ? loaded : undefined
    },
    initialPageParam: 0,
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
}

export function useAlertDetail(id: string | null) {
  return useQuery({
    queryKey: ['alert', id],
    queryFn: () => fetchAlert(id!),
    enabled: !!id,
    staleTime: 60_000,
  })
}

export function useAlertGeoJSON(id: string | null) {
  return useQuery({
    queryKey: ['alert-geojson', id],
    queryFn: () => fetchAlertGeoJSON(id!),
    enabled: !!id,
    staleTime: 120_000,
  })
}
